import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
    X, Sparkles, FileText, Loader2, Copy, FileDown, Check,
    PhoneCall, History, Activity, Pill, CalendarCheck, FolderOpen,
    Heart, MessageCircle, ClipboardCheck, Map as MapIcon, StickyNote, Save,
    ClipboardList, Mail, FileType2,
} from "lucide-react";

const CAT_ICON = {
    behavior: Activity, medication: Pill, appointment: CalendarCheck,
    document: FolderOpen, wellness: Heart, ai: MessageCircle,
    assessment: ClipboardCheck, roadmap: MapIcon, care_plan: FileText, note: StickyNote,
};

const PLAN_FIELDS = [
    ["recommendations", "Today's recommendations"],
    ["medication_changes", "Medication changes"],
    ["safety", "Safety recommendations"],
    ["referrals", "Referrals"],
    ["goals", "Goals"],
    ["next_follow_up", "Next follow-up"],
];

export default function OrgPatientDetail({ orgId, userId, patient, orgName, onClose, onChanged }) {
    const [tab, setTab] = useState("timeline");
    const [timeline, setTimeline] = useState(null);
    const [prep, setPrep] = useState(null);
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState("");
    const [plan, setPlan] = useState(null);

    useEffect(() => {
        setTab("timeline"); setTimeline(null); setPrep(null); setSummary(null); setPlan(null);
        api.get(`/org/intel/${orgId}/patient/${userId}/timeline`)
            .then(r => setTimeline(r.data.events || [])).catch(() => setTimeline([]));
        api.get(`/org/intel/${orgId}/patient/${userId}/care-plan`)
            .then(r => setPlan(r.data || {})).catch(() => setPlan({}));
    }, [orgId, userId]);

    const generate = async (kind) => {
        setBusy(kind);
        try {
            const r = await api.post(`/org/intel/${orgId}/patient/${userId}/${kind}`);
            if (kind === "visit-prep") { setPrep(r.data); setTab("prep"); }
            else { setSummary(r.data); setTab("summary"); }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed.");
        } finally { setBusy(""); }
    };

    const [contactOpen, setContactOpen] = useState(false);
    const [contactNote, setContactNote] = useState("");

    const logContact = async () => {
        try {
            await api.post(`/org/intel/${orgId}/patient/${userId}/contact`, { note: contactNote.trim() });
            toast.success("Contact logged.");
            setContactOpen(false);
            setContactNote("");
            onChanged && onChanged();
        } catch { toast.error("Couldn't log contact."); }
    };

    const savePlan = async () => {
        setBusy("plan");
        try {
            const body = Object.fromEntries(PLAN_FIELDS.map(([k]) => [k, plan[k] || ""]));
            const r = await api.put(`/org/intel/${orgId}/patient/${userId}/care-plan`, body);
            setPlan(r.data);
            toast.success("Care plan updated — the family sees this immediately.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save plan.");
        } finally { setBusy(""); }
    };

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="org-patient-detail">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <div className="w-full max-w-3xl h-full bg-cream overflow-y-auto shadow-2xl">
                <div className="sticky top-0 z-10 bg-white border-b border-cream-dark px-6 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <p className="text-lg font-heading font-semibold text-[#1F2624]">{patient?.patient_name || "Patient"}</p>
                            <p className="text-xs text-[#6E7A74]">
                                Caregiver: {patient?.caregiver_name}
                                {patient?.fast_stage ? ` · FAST ${patient.fast_stage} (caregiver-reported${patient?.fast_date ? `, ${patient.fast_date}` : ""})` : " · FAST —"}
                                {patient?.zarit != null ? ` · Zarit ${patient.zarit} (caregiver-reported${patient?.zarit_date ? `, ${patient.zarit_date}` : ""})` : " · Zarit —"}
                            </p>
                        </div>
                        <button type="button" onClick={onClose} data-testid="org-patient-close" className="text-[#6E7A74] hover:text-[#1F2624]">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                        <button type="button" onClick={() => generate("visit-prep")} disabled={!!busy} data-testid="generate-visit-prep-btn"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-teal text-white hover:bg-teal-dark disabled:opacity-60">
                            {busy === "visit-prep" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate Visit Prep
                        </button>
                        <button type="button" onClick={() => generate("clinical-summary")} disabled={!!busy} data-testid="generate-clinical-summary-btn"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-white border border-cream-dark text-[#1F2624] hover:border-teal disabled:opacity-60">
                            {busy === "clinical-summary" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Generate Clinical Summary
                        </button>
                        <button type="button" onClick={() => setContactOpen(true)} data-testid="log-contact-btn"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal">
                            <PhoneCall size={14} /> Log contact
                        </button>
                    </div>
                    {contactOpen && (
                        <div className="mt-3 rounded-2xl border border-cream-dark bg-cream/60 p-4" data-testid="log-contact-panel">
                            <label className="text-xs font-semibold text-[#4C5551]">What was done? (optional)</label>
                            <textarea rows={2} value={contactNote} onChange={(e) => setContactNote(e.target.value)}
                                placeholder="e.g., Exported clinical summary and documented in our EHR. Called caregiver re: sleep changes."
                                className="ml-input mt-1 text-sm !py-2 w-full" data-testid="log-contact-note" />
                            <div className="mt-2 flex gap-2">
                                <button type="button" onClick={logContact} data-testid="log-contact-save"
                                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-2xl text-xs font-semibold bg-teal text-white hover:bg-teal-dark">
                                    Save contact
                                </button>
                                <button type="button" onClick={() => setContactOpen(false)} data-testid="log-contact-cancel"
                                    className="px-3 py-1.5 rounded-2xl text-xs font-semibold text-[#6E7A74]">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="mt-3 flex gap-1.5">
                        {[["timeline", "Timeline"], ["prep", "Visit Prep"], ["summary", "Clinical Summary"], ["plan", "Care Plan"]].map(([k, label]) => (
                            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`org-patient-tab-${k}`}
                                className={`px-3.5 py-1.5 rounded-2xl text-xs font-semibold ${tab === k ? "bg-[#1F2624] text-white" : "bg-cream text-[#4C5551]"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6">
                    {tab === "timeline" && <TimelineTab events={timeline} />}
                    {tab === "prep" && <GeneratedDoc doc={prep} kind="Visit Prep" docType="visit_prep" orgId={orgId} userId={userId} patient={patient} orgName={orgName} emptyHint="Hit 'Generate Visit Prep' — Lane summarizes everything since the last contact and suggests today's top 5 priorities." />}
                    {tab === "summary" && <GeneratedDoc doc={summary} kind="Clinical Summary" docType="clinical_summary" orgId={orgId} userId={userId} patient={patient} orgName={orgName} emptyHint="Hit 'Generate Clinical Summary' — a sectioned interval note ready to paste into your EHR." />}
                    {tab === "plan" && plan !== null && (
                        <div className="space-y-3" data-testid="care-plan-editor">
                            <p className="text-xs text-[#6E7A74]">
                                The family sees this immediately in their Memory Lane, in caregiver-friendly language.
                                {plan.updated_at ? ` Last updated ${new Date(plan.updated_at).toLocaleString()} by ${plan.updated_by}.` : ""}
                            </p>
                            {PLAN_FIELDS.map(([k, label]) => (
                                <div key={k}>
                                    <label className="text-xs font-semibold text-[#4C5551]">{label}</label>
                                    <textarea rows={2} className="ml-input mt-1 text-sm !py-2" value={plan[k] || ""}
                                        onChange={e => setPlan(p => ({ ...p, [k]: e.target.value }))}
                                        data-testid={`care-plan-${k}`} />
                                </div>
                            ))}
                            <button type="button" onClick={savePlan} disabled={busy === "plan"} data-testid="care-plan-save"
                                className="ml-btn-primary text-sm">
                                {busy === "plan" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Publish to family
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TimelineTab({ events }) {
    if (events === null) return <p className="text-sm text-[#6E7A74]">Loading the story…</p>;
    if (!events.length) return <p className="text-sm text-[#4C5551]" data-testid="org-timeline-empty">Nothing shared yet — the family controls what's visible.</p>;
    return (
        <ul className="space-y-2.5" data-testid="org-timeline">
            {events.map((e, i) => {
                const Icon = CAT_ICON[e.category] || StickyNote;
                return (
                    <li key={i} className="flex items-start gap-3 bg-white border border-cream-dark rounded-2xl p-3.5">
                        <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-cream-dark/50 text-[#4C5551] shrink-0">
                            <Icon size={14} strokeWidth={1.8} />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1F2624]">{e.what}</p>
                            {e.detail && <p className="text-xs text-[#4C5551] mt-0.5">{e.detail}</p>}
                            <p className="text-[11px] text-[#6E7A74] mt-0.5">{(e.date || "").slice(0, 10)} · {e.added_by || "Caregiver"}</p>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

const KNOWN_HEADERS = [
    "SINCE LAST VISIT", "SUGGESTED PRIORITIES",
    "Interval History", "Behavior Changes", "Functional Changes", "Hospitalizations",
    "Medication Changes", "Caregiver Concerns", "Caregiver-Reported Measures",
    "Validated Assessments", "AI Summary", "Suggested Plan",
];

function parseSections(text) {
    const lines = (text || "").split("\n");
    const isHeader = (l) => {
        const t = l.trim().replace(/:$/, "");
        return t && KNOWN_HEADERS.some(h => t.toLowerCase() === h.toLowerCase());
    };
    const sections = [];
    let cur = { title: "Overview", lines: [] };
    for (const l of lines) {
        if (isHeader(l)) {
            if (cur.lines.some(x => x.trim())) sections.push(cur);
            cur = { title: l.trim().replace(/:$/, ""), lines: [] };
        } else {
            cur.lines.push(l);
        }
    }
    if (cur.lines.some(x => x.trim()) || cur.title !== "Overview") sections.push(cur);
    return sections;
}

// EHR-safe: strip bullet characters and markdown symbols that break when
// pasted into a plain EHR text field.
const ehrSafe = (l) => l.replace(/^\s*[-•*·]\s+/, "").replace(/[*_#`>|]/g, "").trimEnd();

function GeneratedDoc({ doc, kind, docType, orgId, userId, patient, orgName, emptyHint }) {
    const { user } = useAuth();
    const [copied, setCopied] = useState("");
    const [busy, setBusy] = useState("");
    const sections = useMemo(() => parseSections(doc?.text), [doc?.text]);
    const [checked, setChecked] = useState({});
    useEffect(() => {
        setChecked(Object.fromEntries(sections.map(s => [s.title, true])));
    }, [sections]);

    if (!doc) return <p className="text-sm text-[#4C5551]" data-testid="generated-doc-empty">{emptyHint}</p>;

    const navName = user?.name || user?.email || "Navigator";
    const included = sections.filter(s => checked[s.title]);
    const includedTitles = included.map(s => s.title);
    const selectedRaw = included
        .map(s => (s.title === "Overview" ? s.lines.join("\n") : `${s.title}\n${s.lines.join("\n")}`))
        .join("\n\n").replace(/\n{3,}/g, "\n\n").trim();

    const stamp = () => new Date().toLocaleString();
    const headerBlock = () =>
        `${kind} — ${orgName}\nPatient: ${patient?.patient_name || "Loved one"} · Caregiver: ${patient?.caregiver_name || "Caregiver"}\nGenerated ${stamp()} by ${navName}\nCaregiver-reported. Not a medical record.\n\n`;

    const logExport = (format) =>
        api.post(`/org/intel/${orgId}/patient/${userId}/export`,
            { doc_type: docType, format, content: selectedRaw, sections: includedTitles }).catch(() => {});

    const requireSelection = () => {
        if (!selectedRaw) { toast.error("Select at least one section to export."); return false; }
        return true;
    };

    const copyPlain = async () => {
        if (!requireSelection()) return;
        const plain = headerBlock() + selectedRaw.split("\n").map(ehrSafe).join("\n");
        try {
            await navigator.clipboard.writeText(plain);
            setCopied("plain");
            logExport("copy");
            toast.success("Copied — EHR-safe plain text.");
            setTimeout(() => setCopied(""), 2000);
        } catch { toast.error("Couldn't copy."); }
    };

    const copyEhrNote = async () => {
        if (!requireSelection()) return;
        const parts = included.map(s => {
            const items = s.lines.map(ehrSafe).map(t => t.trim().replace(/[.;]\s*$/, "")).filter(Boolean);
            if (!items.length) return "";
            return s.title === "Overview" ? `${items.join("; ")}.` : `${s.title}: ${items.join("; ")}.`;
        }).filter(Boolean);
        const prose = `Caregiver-reported via Memory Lane (${orgName}); not a medical record. Generated ${stamp()} by ${navName}. ` + parts.join(" ");
        try {
            await navigator.clipboard.writeText(prose);
            setCopied("ehr");
            logExport("ehr");
            toast.success("Copied as a continuous EHR note.");
            setTimeout(() => setCopied(""), 2000);
        } catch { toast.error("Couldn't copy."); }
    };

    const download = async (format) => {
        if (!requireSelection()) return;
        setBusy(format);
        try {
            const r = await api.post(`/org/intel/${orgId}/patient/${userId}/export`,
                { doc_type: docType, format, content: selectedRaw, sections: includedTitles },
                { responseType: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(r.data);
            const ext = format === "pdf" ? "pdf" : "docx";
            a.download = `${kind.toLowerCase().replace(/ /g, "-")}-${new Date().toISOString().slice(0, 10)}.${ext}`;
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success(format === "pdf" ? "PDF downloaded." : "Word document downloaded.");
        } catch { toast.error("Export failed."); } finally { setBusy(""); }
    };

    const emailMe = async () => {
        if (!requireSelection()) return;
        setBusy("email");
        try {
            const r = await api.post(`/org/intel/${orgId}/patient/${userId}/export`,
                { doc_type: docType, format: "email", content: selectedRaw, sections: includedTitles });
            if (r.data?.sent) toast.success(`Sent to ${r.data.to} — your account email only.`);
            else toast.error("Email isn't configured in this environment — the export was still logged.");
        } catch { toast.error("Couldn't send."); } finally { setBusy(""); }
    };

    const btn = "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal disabled:opacity-50";

    return (
        <div data-testid="generated-doc">
            {sections.length > 1 && (
                <div className="mb-3 rounded-2xl border border-cream-dark bg-white p-3.5" data-testid="export-section-selector">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">Sections to export</p>
                    <div className="flex gap-x-4 gap-y-1.5 flex-wrap">
                        {sections.map(s => (
                            <label key={s.title} className="inline-flex items-center gap-1.5 text-xs text-[#1F2624] cursor-pointer">
                                <input type="checkbox" checked={!!checked[s.title]}
                                    onChange={(e) => setChecked(c => ({ ...c, [s.title]: e.target.checked }))}
                                    data-testid={`export-section-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="accent-[#12776F]" />
                                {s.title}
                            </label>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex gap-2 flex-wrap mb-3">
                <button type="button" onClick={copyPlain} data-testid="generated-doc-copy" className={btn}>
                    {copied === "plain" ? <Check size={12} /> : <Copy size={12} />} Copy
                </button>
                <button type="button" onClick={copyEhrNote} data-testid="generated-doc-copy-ehr" className={btn}>
                    {copied === "ehr" ? <Check size={12} /> : <ClipboardList size={12} />} Copy as EHR note
                </button>
                <button type="button" onClick={() => download("pdf")} disabled={!!busy} data-testid="generated-doc-pdf" className={btn}>
                    {busy === "pdf" ? <Loader2 size={12} className="animate-spin" /> : <FileType2 size={12} />} PDF
                </button>
                <button type="button" onClick={() => download("docx")} disabled={!!busy} data-testid="generated-doc-word" className={btn}>
                    {busy === "docx" ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} Word
                </button>
                <button type="button" onClick={emailMe} disabled={!!busy} data-testid="generated-doc-email" className={btn}>
                    {busy === "email" ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Email to myself
                </button>
            </div>
            <p className="text-[11px] text-[#6E7A74] mb-3" data-testid="export-disclaimer-note">
                Every export is stamped “Caregiver-reported. Not a medical record.” with the date, your name,
                and source labels — and is logged to the family&apos;s access audit. Email goes to your account
                address only.
            </p>
            <pre className="bg-white border border-cream-dark rounded-2xl p-5 text-sm text-[#1F2624] whitespace-pre-wrap font-sans leading-relaxed" data-testid="generated-doc-text">
                {doc.text}
            </pre>
        </div>
    );
}
